/**
 * AriaOrb — particle-free CSS blob.
 *
 * Previous version was a Three.js particle cloud (2,470 particles + bloom
 * pass running 60fps) — too heavy on CPU/GPU for what it is. Replaced with
 * a single CSS radial gradient that smoothly pulses when Aria speaks or
 * listens. No WebGL, no animation frames running, no GPU work at rest.
 *
 * Props the rest of the app passes (kept for API compatibility — most are
 * now ignored):
 *   speaking    — true when Aria is speaking (orb glows brighter, pulses faster)
 *   listening   — true when Aria is listening (orb glows blue tint)
 *   audioRef    — ignored (was for real-time FFT visualization)
 *   size        — display size in px (default 280)
 *   fill        — when true, fills its container instead of using size
 *   className   — extra class
 */
import React from 'react';

function AriaOrb({ speaking = false, listening = false, size = 280, fill = false, className = '' }) {
  // Visual state derives from speaking/listening — no rAF loop, no JS state.
  // CSS animations handle the pulse; they cost ~0 CPU because the browser
  // composites them on the GPU layer-by-layer.
  const state = speaking ? 'speaking' : listening ? 'listening' : 'idle';

  const dims = fill
    ? { width: '100%', height: '100%' }
    : { width: size, height: size };

  return (
    <div
      className={`aria-orb aria-orb--${state} ${className}`}
      style={{
        ...dims,
        position: 'relative',
        display: 'inline-block',
        pointerEvents: 'none',
      }}
      aria-hidden="true"
    >
      <style>{`
        .aria-orb {
          --orb-color-idle:    rgba(124, 200, 160, 1);
          --orb-color-speak:   rgba(124, 224, 200, 1);
          --orb-color-listen:  rgba(124, 183, 232, 1);
        }
        .aria-orb__core {
          position: absolute;
          inset: 18%;
          border-radius: 50%;
          background: radial-gradient(circle at 35% 30%,
            rgba(255,255,255,0.55) 0%,
            var(--orb-color, var(--orb-color-idle)) 22%,
            rgba(40,80,70,0.92) 70%,
            rgba(10,20,18,1) 100%);
          box-shadow:
            inset -8px -12px 24px rgba(0,0,0,0.55),
            inset  6px  8px 18px rgba(255,255,255,0.18);
          transition: background 0.5s ease, transform 0.4s ease;
          will-change: transform, opacity;
        }
        .aria-orb__halo {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: radial-gradient(circle,
            var(--orb-color, var(--orb-color-idle)) 0%,
            rgba(0,0,0,0) 60%);
          opacity: 0.35;
          filter: blur(14px);
          transition: opacity 0.5s ease, filter 0.5s ease;
          will-change: opacity;
        }
        .aria-orb--idle      { --orb-color: var(--orb-color-idle);   }
        .aria-orb--listening { --orb-color: var(--orb-color-listen); }
        .aria-orb--speaking  { --orb-color: var(--orb-color-speak);  }

        /* Idle: very slow gentle breath, ~0% CPU */
        .aria-orb--idle .aria-orb__core { animation: aria-orb-breathe 5.5s ease-in-out infinite; }
        .aria-orb--idle .aria-orb__halo { animation: aria-orb-halo    5.5s ease-in-out infinite; }

        /* Listening: a touch faster, blue tint */
        .aria-orb--listening .aria-orb__core { animation: aria-orb-breathe 2.2s ease-in-out infinite; }
        .aria-orb--listening .aria-orb__halo { animation: aria-orb-halo    2.2s ease-in-out infinite; opacity: 0.55; }

        /* Speaking: faster pulse + brighter halo */
        .aria-orb--speaking  .aria-orb__core { animation: aria-orb-speak   1.0s ease-in-out infinite; }
        .aria-orb--speaking  .aria-orb__halo { animation: aria-orb-halo    1.0s ease-in-out infinite; opacity: 0.7;  filter: blur(20px); }

        @keyframes aria-orb-breathe {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.025); }
        }
        @keyframes aria-orb-speak {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.07); }
        }
        @keyframes aria-orb-halo {
          0%, 100% { opacity: 0.30; }
          50%      { opacity: 0.50; }
        }

        @media (prefers-reduced-motion: reduce) {
          .aria-orb__core, .aria-orb__halo { animation: none !important; }
        }
      `}</style>
      <div className="aria-orb__halo" />
      <div className="aria-orb__core" />
    </div>
  );
}

export default AriaOrb;
